import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api } from "../api";
import { useNav } from "../hooks/useNav";
import Topbar from "./Topbar";
import ReplyPanel from "./ReplyPanel";
import ContactDetailPage from "./ContactDetailPage";
import useEscapeLayer from "./useEscape";
import { BOARD_STAGE_LABELS, moneyK, dateShort, moneyMeaningTitle } from "./ui";
// The snooze menu's wording, shared with the board's own hold menu so the two name the
// same five windows. The DATES come from the server — two clients computing "next
// Monday" off their own clocks is how one row lands on a Sunday and another on a Monday.
import { SNOOZE_LABELS } from "./hold.mjs";
import Timeline from "./Timeline";
// Remove from pipeline… is the record's own ending, confirm and all.
import ConfirmCard from "./record/ConfirmCard";
import { canRemove, withRowWrites } from "./followUpsRemove.mjs";
import DateField from "./datepicker/DateField";
import { todayYmd } from "./datepicker/calendar.mjs";

/**
 * FOLLOW-UPS v2 — what has been let sit, in the order it is worth doing.
 *
 * Implemented against Gabriel's Claude Design mockup (Follow-ups.dc.html). Three screens
 * live here: the queues, the muted table, and the all-clear state. The rule builder is
 * its own route.
 *
 * THE PAGE COMPUTES NOTHING. Every number, sentence and chip on a row arrives from
 * GET /api/follow-ups already shaped — the score and its breakdown, the "why this?" text,
 * the second-line summary of the last touch, the snooze presets and ignore reasons. That
 * is the same rule the board and Today follow (see the note at the top of
 * src/actions/model.js): a copy of any of this in React is the one way the surfaces drift
 * apart. What the page owns is selection, which popover is open, and which screen you are
 * looking at.
 *
 * WHY THE ROWS ARE STABLE THROUGH THE DAY. A capped queue renders today's BATCH, chosen
 * on the first view of the day and held (src/db/followUpBatch.js). Working a row removes
 * it and nothing takes its place until tomorrow, which is what lets a queue be finished
 * rather than merely scrolled. It also means the mockup's "checks every weekday at 7:00"
 * is not what happens — there is no scheduled job — so the copy here says what does.
 */

// The two pinned sections, in the order the mockup fixes them. Scheduled first: a promise
// Gabriel made to himself outranks a pattern a rule noticed.
const PINNED = [
  {
    key: "scheduled",
    title: "Scheduled for today",
    note: "you set these dates yourself",
  },
  {
    key: "owed",
    title: "You owe a reply",
    note: "they wrote last",
  },
];

const IGNORE_LABELS = { 30: "30 days", 60: "60 days", 90: "90 days", 180: "6 months" };

const REASON_LABELS = {
  too_small: "Too small",
  wrong_icp: "Wrong ICP",
  agency: "Agency",
  competitor: "Lost to a competitor",
  bad_fit: "Not a fit",
  passed: "I passed on it",
  wrong_contact: "Wrong contact",
  handled_elsewhere: "Handled outside the CRM",
  other: "Other",
  // Not an ignore reason — the ignore picker reads its options from the server
  // (vocabulary.ignore_reasons) and never offers this one. It is here for the Muted
  // filter, where a derived auto-close suppression files itself under the
  // status_reason the ghost sweep proposes.
  ghosted: "Auto-close pending",
};

/**
 * "synced 7 min ago". Minutes for the first hour, then hours, then the date — the
 * resolution you care about collapses as it ages, and "synced 4,140 min ago" is a number
 * nobody reads.
 */
function syncedLabel(at) {
  if (!at) return "never synced";
  const mins = Math.max(0, Math.round((Date.now() - new Date(at).getTime()) / 60000));
  if (mins < 1) return "synced just now";
  if (mins < 60) return `synced ${mins} min ago`;
  if (mins < 60 * 24) return `synced ${Math.round(mins / 60)} h ago`;
  return `synced ${dateShort(at)}`;
}

/**
 * WHERE THIS RULE'S OTHER MATCHES WENT — "All 3 matches are shown in Deal gone quiet,
 * which scores them higher."
 *
 * A company is drawn once, in the queue that scores it highest (the ruling is in
 * src/actions/index.js). So a rule's count and its row count can legitimately differ, and
 * without this line the gap reads as a lost row rather than as a decision. The queue that
 * took them is named because that is the actionable half: the work is still on the page,
 * under the heading that describes it best.
 */
function elsewhereNote(q) {
  const parts = (q.elsewhere || []).map((e) => `${e.count} in ${e.name}`);
  if (!parts.length) return "";
  if (parts.length === 1) {
    const all = q.shown_elsewhere === q.count ? "All " : "";
    const [{ count, name }] = q.elsewhere;
    return `${all}${count} match${count === 1 ? " is" : "es are"} shown in ${name}, which scores ${count === 1 ? "it" : "them"} higher.`;
  }
  const joined = `${parts.slice(0, -1).join(", ")} and ${parts[parts.length - 1]}`;
  return `Shown elsewhere: ${joined} — those queues score them higher.`;
}

/** "Mon, Sep 1" — read at render so a tab left open overnight doesn't insist on yesterday. */
const TODAY_LONG = () =>
  new Date().toLocaleDateString("en-US", { weekday: "short", month: "short", day: "numeric" });

// ------------------------------------------------------------------ the row

/**
 * One company, one row. The grammar is fixed by the mockup:
 *
 *   [x] Company · stage · POC · quiet-since · chased ..... chips  Compose Snooze Ignore ✓
 *       what the last touch was · why this?
 *       [your note, when it was written]
 *
 * THE WHOLE ROW IS THE OPEN CONTROL. There was an Open button on the right; it is gone,
 * because a row you can click is what everyone tries first and the button was spending
 * 52px of the tightest line in the product saying so. The controls keep their own clicks
 * (a click that lands on Compose is a compose, never an open), and the company name is a
 * real button so the keyboard and a screen reader have the same affordance the pointer
 * has.
 *
 * The chips come in three weights and the distinction is the design's, not decoration:
 * yellow-filled is "you, now", red-outlined is a warning about something that will happen
 * without you, and grey-outlined is a status.
 */
function Row({ row, section, ui, actions }) {
  const stage = row.pipeline_stage ? BOARD_STAGE_LABELS[row.pipeline_stage] : null;
  const value = row.monthly_value ? moneyK(row.monthly_value, row.currency) : null;
  // "quiet 26d" for a queue row; a pinned reply debt is phrased as a wait, because the
  // clock means something different when someone is on the other end of it.
  const clock =
    section === "owed"
      ? `waiting ${row.quiet_days}d`
      : section === "scheduled"
        ? `promised ${dateShort(row.snoozed_until)}`
        : `quiet ${row.quiet_days}d`;

  const open = ui.open;

  /*
   * A click on the row opens it — unless it landed on something that owns its own click.
   *
   * The closest() test lists the controls and the two things the row expands INTO: a
   * click inside the snooze popover or the opened record is not a click on the row, and
   * without that the first thing you do inside a panel closes it.
   *
   * A drag that selected text is reading, not clicking. Without the check, highlighting
   * a company name to copy it collapses the record underneath.
   */
  const onRowClick = (e) => {
    if (e.target.closest("button, a, input, textarea, select, label, .crm-fu__pop, .crm-fu__detail, .crm-fu__why-panel")) return;
    const sel = typeof window !== "undefined" && window.getSelection();
    if (sel && String(sel).trim() && sel.type === "Range") return;
    actions.rowClick(row);
  };

  // Only clickable when there is something to open. A company with no contact has no
  // record to show, and a pointer cursor over a row that does nothing is a broken row.
  const openable = !!row.contact_id;

  return (
    <div
      className={`crm-fu__row${ui.checked ? " crm-fu__row--sel" : ""}${openable ? " crm-fu__row--open" : ""}`}
      onClick={onRowClick}
    >
      <div className="crm-fu__line">
        <button
          type="button"
          className={`crm-fu__check${ui.checked ? " is-on" : ""}`}
          onClick={() => actions.toggleCheck(row.company_id)}
          aria-label={ui.checked ? "Deselect" : "Select"}
        >
          {ui.checked ? "✓" : ""}
        </button>

        {openable ? (
          <button
            type="button"
            className="crm-fu__co crm-fu__co--btn"
            onClick={() => actions.openMenu(row.company_id, "detail")}
            aria-expanded={ui.open === "detail"}
            title={ui.open === "detail" ? "Close the record" : "Open the record"}
          >
            {row.company_name || row.company_domain || "(unnamed)"}
          </button>
        ) : (
          <span className="crm-fu__co">{row.company_name || row.company_domain || "(unnamed)"}</span>
        )}
        {stage && <span className="crm-fu__stage">{stage}</span>}
        <span className="crm-fu__poc">{row.contact_name || row.contact_email || "—"}</span>
        <span className="crm-fu__quiet crm-num">{clock}</span>
        {/*
          THEIR SILENCE, THEN OURS. quiet-since counts how long they have said nothing;
          this counts how long since we last chased, and the pair is the actual decision
          — "quiet 60d, chased 2d" is a row to leave alone today however old it looks.
          It is also what the ranking now uses (the chase cooloff in rules.js), so the
          fact that moves a row down is on the row that moved.
        */}
        <span className="crm-fu__chased crm-num">{row.chase_line}</span>
      </div>

      {/*
        THE CONTROLS ARE A CELL OF THE ROW, not the tail of the identity line — which is
        what lets them move without the DOM moving. On a full-width row the grid puts them
        top-right, beside the identity, exactly where they have always been. When the row
        narrows (the record panel is open, or a small window) they drop to sit beside the
        touch line instead of taking a line of their own: the same information in two lines
        rather than three, and no band of dead space where the identity used to end.
      */}
      <span className="crm-fu__acts">
        <span className="crm-fu__chips">
          {/*
            ONE chip that says something the section does not already say.

            The first version showed the top action's label, and against real data that
            printed "reply waiting" on nearly every row of "Warm, never chased" and "We
            went quiet on them" — queues whose whole definition is that they replied and
            nobody answered. A chip repeating its own section header is furniture.

            So: in a pinned section the action IS the news, and in a rule queue the rule's
            own chip is.

            THERE USED TO BE AN "auto-close pending" CHIP ABOVE THESE, and it is gone
            because a row can no longer be in both states. It read row.ghost.ghosted —
            true for a company the weekly sweep is about to propose closing — while the
            section around it asked for a chase, and Cynomi shipped both: quiet 118 days,
            the deal ended in May, the chip said it was being written off and the queue
            said to email them. That is settled in the QUEUE now (scope.autoClosePending:
            a flagged row is off every queue and listed in Muted under that reason), so
            the chip here would be unreachable furniture. The state is still shown — on
            the screen where it is now the truth.
          */}
          {row.pin_chip ? (
            /* "You promised today", "snoozed 2×", "reply owed 2d" — the mockup's own
               words, shaped by the server with the tone that carries their meaning. */
            <span className={`crm-fu__chip crm-fu__chip--${row.pin_chip.tone}`}>{row.pin_chip.label}</span>
          ) : (
            row.why &&
            row.why.chip_label && <span className="crm-fu__chip crm-fu__chip--do">{row.why.chip_label}</span>
          )}
        </span>

        <span className="crm-fu__btns">
            {row.contact_email && (
              <button type="button" className="crm-fu__btn crm-fu__btn--do" onClick={() => actions.compose(row, section)}>
                Compose
              </button>
            )}
            <button type="button" className="crm-fu__btn" onClick={() => actions.openMenu(row.company_id, "snooze")}>
              Snooze ▾
            </button>
            <button type="button" className="crm-fu__btn crm-fu__btn--quiet" onClick={() => actions.openMenu(row.company_id, "ignore")}>
              Ignore ▾
            </button>
            {/*
              A TICK, NOT A WORD. Measured: five word-buttons took 392px of an 860px line,
              which squeezed the POC to zero on every full row. Mark worked is the newest
              of the five and the only one the mockup does not have, so it is the one that
              gives up its label rather than the row giving up a field. Still one click,
              still 'w' on the keyboard, and the title says what it does.
            */}
            <button
              type="button"
              className="crm-fu__btn crm-fu__btn--quiet crm-fu__btn--icon"
              title="Mark worked (w)"
              aria-label="Mark worked"
              onClick={() => actions.markWorked(row.company_id)}
            >
            ✓
          </button>
        </span>
      </span>

      <div className="crm-fu__second">
        {/* Moved down from line one: it says where else this row appears, which is meta
            about the page rather than news about the company — and two chips on the top
            line is what overflowed the chip track. */}
        {row.also_pinned && <span className="crm-fu__pinnote">pinned above</span>}
        {/*
          MONEY SITS HERE, not on the line above — which is where the mockup puts it too,
          and the first version got wrong. Line one is identity plus controls, and adding
          a fifth thing to it meant the chips had to give way: against real rows they
          ellipsised to "no cha…", "referr…", "we w…", which is a label that has stopped
          being a label. Bold on the second line it still scans down the page, and it
          reads as what it is — context for the decision, not part of the name.
        */}
        {value && <span className="crm-fu__val crm-num" title={moneyMeaningTitle(row.status)}>{value}</span>}
        <span className="crm-fu__touch">{row.touch_line}</span>
        {row.why && (
          <button type="button" className="crm-fu__why" onClick={() => actions.openMenu(row.company_id, "why")}>
            why this?
          </button>
        )}
      </div>

      {/* The note Gabriel left himself when he parked it, shown at the moment it is
          useful — which is when the company wakes up, long after he would remember. */}
      {row.snooze_note && (
        <div className="crm-fu__note">
          {/* Dated, because the note is read weeks after it was written and "what did I
              know when I said that" is half of what it is for. */}
          <b>Your note{row.snooze_note_at ? `, ${dateShort(row.snooze_note_at)}` : ""}:</b>{" "}
          {row.snooze_note}
        </div>
      )}

      {open === "why" && (
        <div className="crm-fu__why-panel">
          <div className="crm-fu__why-head">Matched by rule</div>
          <div className="crm-fu__why-rule">{row.why.name}</div>
          <div className="crm-fu__why-detail">{row.why_detail}</div>
          <div className="crm-fu__why-links">
            <button type="button" onClick={() => actions.openMenu(row.company_id, null)}>close</button>
            <Link to={`/follow-ups/rules/${row.why.rule_id}`}>edit this rule</Link>
          </div>
        </div>
      )}

      {open === "detail" && <RowDetail row={row} onFullRecord={() => actions.openRecord(row, section)} />}

      {open === "snooze" && <SnoozeMenu row={row} actions={actions} vocabulary={ui.vocabulary} />}
      {open === "ignore" && <IgnoreMenu row={row} section={section} actions={actions} vocabulary={ui.vocabulary} />}
    </div>
  );
}

/**
 * The record, opened where the row is.
 *
 * Loaded on demand rather than with the page: forty rows' timelines is a lot of query for
 * something you look at one of. Fetched once and kept, so toggling a row shut and open
 * again is instant.
 *
 * DELIBERATELY NOT THE WHOLE CONTACT PAGE. What you need mid-queue is the last exchange
 * and what has happened lately — enough to decide whether to write, snooze or let go. The
 * editing, the firmographics and the stage controls stay one link away, because bringing
 * them here would make the row a second contact page and the page a worse queue.
 *
 * "Full record" used to be that link, and following it left the page — which on a capped
 * batch means losing today's list. It now opens the record in the panel beside the queue
 * (Gabriel's note: "why not have it open to the right of the current thing here"), the
 * same split Approvals uses to rule on a change with the record in view. The panel is
 * the real contact page, embedded; a second copy of it would be a second version of the
 * truth.
 */
function RowDetail({ row, onFullRecord }) {
  const [state, setState] = useState({ loading: true });

  useEffect(() => {
    let live = true;
    (async () => {
      try {
        const [contact, tl] = await Promise.all([
          api(`/contacts/${row.contact_id}`),
          api(`/contacts/${row.contact_id}/timeline`),
        ]);
        if (live) setState({ loading: false, contact, timeline: tl.timeline || [] });
      } catch (err) {
        if (live) setState({ loading: false, error: err.message });
      }
    })();
    return () => {
      live = false;
    };
  }, [row.contact_id]);

  if (state.loading) return <div className="crm-fu__detail crm-fu__detail--wait">Opening {row.company_name}…</div>;
  if (state.error) return <div className="crm-fu__detail crm-fu__detail--wait">Could not open it: {state.error}</div>;

  const c = state.contact || {};
  const who = [c.first_name, c.last_name].filter(Boolean).join(" ") || c.email;

  return (
    <div className="crm-fu__detail">
      <div className="crm-fu__detail-head">
        <span className="crm-fu__detail-who">{who}</span>
        {c.job_title && <span className="crm-fu__detail-title">{c.job_title}</span>}
        {c.email && <a className="crm-fu__detail-mail" href={`mailto:${c.email}`}>{c.email}</a>}
        <button type="button" className="crm-fu__detail-full" onClick={onFullRecord}>
          full record →
        </button>
      </div>

      <div className="crm-fu__detail-body">
        <Timeline items={state.timeline} contactLabel={who} companyName={row.company_name} contactId={c.id} />
      </div>
    </div>
  );
}

// --------------------------------------------------------------- the menus

function SnoozeMenu({ row, actions, vocabulary }) {
  const [note, setNote] = useState("");
  const [date, setDate] = useState("");

  return (
    <div className="crm-fu__pop crm-fu__pop--snooze">
      <div className="crm-fu__pop-head">Snooze until</div>
      {(vocabulary.snooze_presets || []).map((preset) => (
        <button
          key={preset}
          type="button"
          className="crm-fu__pop-item"
          onClick={() => actions.snooze(row.company_id, { preset, note })}
        >
          <span>{SNOOZE_LABELS[preset] || preset}</span>
          {preset === "until_reply" && (
            <span className="crm-fu__pop-hint">
              no date · comes back after {vocabulary.reply_snooze_backstop_days}d
            </span>
          )}
        </button>
      ))}

      <div className="crm-fu__pop-date">
        <span>Pick a date</span>
        <DateField value={date} onChange={setDate} min={todayYmd()} aria-label="Snooze until" />
        <button type="button" className="crm-fu__btn" disabled={!date} onClick={() => actions.snooze(row.company_id, { until: date, note })}>
          Set
        </button>
      </div>

      <textarea
        className="crm-fu__pop-note"
        placeholder="Note to self (optional) — what should be true by then?"
        value={note}
        onChange={(e) => setNote(e.target.value)}
      />

      <div className="crm-fu__pop-foot">
        <button type="button" className="crm-fu__btn crm-fu__btn--quiet" onClick={() => actions.openMenu(row.company_id, null)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function IgnoreMenu({ row, section, actions, vocabulary }) {
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  // The reason is REQUIRED, and the button says so by being unusable without one. Every
  // ignore is a labelled row of the negative training set the lead agent will read; an
  // unlabelled one is a company silenced for no recorded purpose.
  const ready = !!reason;

  return (
    <div className="crm-fu__pop crm-fu__pop--ignore">
      <div className="crm-fu__pop-head">Keep out of queues for</div>
      {(vocabulary.ignore_durations || []).map((days) => (
        <button
          key={days}
          type="button"
          className="crm-fu__pop-item"
          disabled={!ready}
          onClick={() => actions.ignore(row.company_id, { days, reason, note })}
        >
          {IGNORE_LABELS[days] || `${days} days`}
        </button>
      ))}
      <button
        type="button"
        className="crm-fu__pop-item crm-fu__pop-item--danger"
        disabled={!ready}
        onClick={() => actions.ignore(row.company_id, { forever: true, reason, note })}
      >
        Forever — mute this company
      </button>
      {/*
        NOT A MUTE: the record's Remove from pipeline… (record/endings.mjs), for
        "I've chased enough". It parks the company off the board and it comes back
        if they write, which no ignore window does. Its reason is asked in its own
        confirm, so it never waits on the one below.
      */}
      {canRemove(row) && (
        <button type="button" className="crm-fu__pop-item" onClick={() => actions.remove(row, section)}>
          <span>Remove from pipeline…</span>
          <span className="crm-fu__pop-hint">parks it, wakes on a reminder</span>
        </button>
      )}

      <div className="crm-fu__pop-reason">
        <div className="crm-fu__pop-label">
          Reason <span>required</span>
        </div>
        <select className="crm-select" value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="">Choose a reason…</option>
          {(vocabulary.ignore_reasons || []).map((r) => (
            <option key={r} value={r}>
              {REASON_LABELS[r] || r}
            </option>
          ))}
        </select>
        <input
          className="crm-input"
          placeholder="Anything to add (optional)"
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
        <div className="crm-fu__pop-hint">
          The reason shows in Muted, and it is what the lead agent will learn from.
        </div>
      </div>

      <div className="crm-fu__pop-foot">
        <button type="button" className="crm-fu__btn crm-fu__btn--quiet" onClick={() => actions.openMenu(row.company_id, null)}>
          Cancel
        </button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ screens

/*
 * WHAT IS OFF THE QUEUES, AND WHY — including the rows nobody muted.
 *
 * Most rows here are Gabriel's own decisions (snooze, ignore) and every one of those
 * has an Un-mute. A DERIVED suppression has no mute to lift: an auto-close-pending row
 * is off the queues because the weekly ghost sweep is proposing to close the company,
 * and the way back is to rule on that proposal or for the company to write. So those
 * rows say what ends them instead of offering a button that would do nothing — and they
 * are listed, because the whole promise of this screen is that nothing is unrecoverably
 * hidden, and a silence that only deepens is the one suppression that never lifts itself.
 */
function MutedTable({ rows, onUnmute }) {
  const [reason, setReason] = useState("");
  const shown = rows.filter((r) => !reason || r.suppression.reason === reason);
  const reasons = [...new Set(rows.map((r) => r.suppression.reason).filter(Boolean))];
  const derived = rows.filter((r) => r.suppression.derived).length;

  return (
    <div>
      <div className="crm-fu__muted-head">
        <div>
          <div className="crm-fu__muted-title">Muted · {rows.length}</div>
          <div className="crm-fu__muted-sub">
            These never enter a queue. Every mute keeps its reason, so a company you passed on
            stays telling apart from one that was never a fit.
            {derived > 0 && (
              <>
                {" "}
                {derived} {derived === 1 ? "is" : "are"} here without a mute: the weekly sweep is
                proposing to close {derived === 1 ? "it" : "them"}, and that ruling is in Approvals.
              </>
            )}
          </div>
        </div>
        {reasons.length > 1 && (
          <select className="crm-select" value={reason} onChange={(e) => setReason(e.target.value)}>
            <option value="">All reasons</option>
            {reasons.map((r) => (
              <option key={r} value={r}>
                {REASON_LABELS[r] || r}
              </option>
            ))}
          </select>
        )}
      </div>

      <div className="crm-fu__table">
        <div className="crm-fu__thead">
          <span>Company</span>
          <span>Reason</span>
          <span>Note</span>
          <span>Expires</span>
          <span />
        </div>
        {shown.map((m) => (
          <div key={m.company_id} className="crm-fu__trow">
            <span className="crm-fu__co">{m.company_name}</span>
            <span>{m.suppression.reason_label || (m.suppression.kind === "snoozed" ? "snoozed" : "—")}</span>
            <span className="crm-fu__tnote">{m.suppression.note || "—"}</span>
            <span className="crm-num">
              {m.suppression.derived && !m.suppression.until
                ? "when you rule on it"
                : m.suppression.forever
                  ? "never"
                  : m.suppression.until_reply
                    ? "when they reply"
                    : dateShort(m.suppression.until)}
            </span>
            <span className="crm-fu__tact">
              {m.suppression.derived ? (
                <Link className="crm-fu__btn" to="/approvals">
                  Approvals
                </Link>
              ) : (
                <button type="button" className="crm-fu__btn" onClick={() => onUnmute(m.company_id)}>
                  Un-mute
                </button>
              )}
            </span>
          </div>
        ))}
        {!shown.length && <div className="crm-fu__empty">Nothing muted.</div>}
      </div>
    </div>
  );
}

/**
 * The all-clear state — reachable only because a capped queue is a fixed daily batch.
 *
 * "Empty" and "finished" are different things and only one of them deserves this, so it
 * renders on rows-worked rather than on an absence of rows: a page with nothing to show
 * because no rule matched is not an achievement.
 */
function AllClear({ counts, onPullMore, pullable }) {
  return (
    <div className="crm-fu__clear">
      <img src="/goal.png" alt="" />
      <div>
        <div className="crm-fu__clear-when">{TODAY_LONG()}</div>
        <h2>Queue's empty. All {counts.worked_today} worked.</h2>
        <p>
          Nothing else is due today. The next batch is chosen the first time you open the CRM
          tomorrow.
        </p>
        {pullable && (
          <div className="crm-fu__clear-acts">
            <button type="button" className="crm-fu__btn crm-fu__btn--do" onClick={() => onPullMore(pullable.rule_id)}>
              Pull {pullable.cap} more from {pullable.name}
            </button>
          </div>
        )}
        <div className="crm-fu__clear-foot">
          {counts.scheduled} scheduled · {counts.muted} muted
        </div>
      </div>
    </div>
  );
}

// -------------------------------------------------------------------- page

export default function FollowUpsPage() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  /*
   * The screen lives in the URL, not in component state. Muted is a place you send
   * yourself back to — "which ones did I pass on in August" — and a view you cannot link
   * to or reload into is one you re-navigate to every time.
   */
  const [params, setParams] = useSearchParams();
  const view = params.get("view") === "muted" ? "muted" : "queues";
  const setView = (next) =>
    setParams(next === "muted" ? { view: "muted" } : {}, { replace: true });
  const [checked, setChecked] = useState({});
  const [menu, setMenu] = useState({ id: null, kind: null });
  const [collapsed, setCollapsed] = useState({});
  const [cursor, setCursor] = useState(0);
  // WHICH rail item is inverted. Exactly one, or none — the inversion means "you are
  // here", and a rail with six highlighted rows says nothing at all.
  const [here, setHere] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [replying, setReplying] = useState(null);
  /*
   * The full record, open beside the queue. Held as { contactId, label } rather than as a
   * row, because the row it came from can leave the page under it — working a company
   * removes it from its batch — and the panel is still worth reading after that.
   */
  const [record, setRecord] = useState(null);
  const recordRef = useRef(null);
  recordRef.current = record;
  // The open record's own guard (ContactDetailPage fills it in): opening another
  // row's record, or Esc from out here, would unmount an edited reply inside it,
  // so that draft asks first.
  const recordGuard = useRef(null);
  const leaveRecord = useCallback((proceed) => (recordGuard.current ? recordGuard.current(proceed) : proceed()), []);
  const [busy, setBusy] = useState(false);
  /*
   * Remove from pipeline…, open over the page: { row, section } while the company
   * and its Next load, then { ..., record } for ConfirmCard (the record's bundle:
   * the company with its people, for "Priya stays", and the open lines that close).
   */
  const [removing, setRemoving] = useState(null);
  const cursorRef = useRef(null);

  const { setFollowUps } = useNav();
  const load = useCallback(async () => {
    try {
      const next = await api("/follow-ups");
      setData(next);
      // The menu's badge is this page's to-do, so hand it over rather than let
      // it wait two minutes for its own poll.
      setFollowUps(next.counts.owed + next.counts.scheduled + next.counts.queued);
      setError(null);
    } catch (err) {
      setError(err.message);
    }
  }, [setFollowUps]);

  useEffect(() => {
    load();
  }, [load]);

  /** Every row on the page, in visual order — what j/k walks and what bulk acts on. */
  const flat = useMemo(() => {
    if (!data) return [];
    const out = [];
    for (const section of PINNED) {
      for (const row of data[section.key] || []) out.push({ row, section: section.key });
    }
    for (const q of data.queues || []) {
      if (!q.renders || collapsed[q.slug]) continue;
      for (const row of q.rows) out.push({ row, section: q.slug });
    }
    return out;
  }, [data, collapsed]);

  const selected = useMemo(() => Object.keys(checked).filter((k) => checked[k]), [checked]);

  // --- the writes ---------------------------------------------------------
  // Every one reloads. The page is a view of derived state — one snooze can change which
  // rules match, what today's batch holds and three counts in the rail — so re-fetching is
  // both simpler and more honest than patching a local copy that would slowly diverge.
  const act = useCallback(
    async (path, body) => {
      setBusy(true);
      try {
        await api(path, { method: "POST", body: JSON.stringify(body || {}) });
        setMenu({ id: null, kind: null });
        await load();
      } catch (err) {
        setError(err.message);
      } finally {
        setBusy(false);
      }
    },
    [load]
  );

  const actions = useMemo(
    () => ({
      toggleCheck: (id) => setChecked((c) => ({ ...c, [id]: !c[id] })),
      openMenu: (id, kind) =>
        setMenu((m) => (m.id === id && m.kind === kind ? { id: null, kind: null } : { id, kind })),
      snooze: (id, body) => act(`/follow-ups/${id}/snooze`, body),
      ignore: (id, body) => act(`/follow-ups/${id}/ignore`, body),
      markWorked: (id) => act(`/follow-ups/${id}/worked`),
      compose: (row, section) => setReplying({ row, section }),
      remove: async (row, section) => {
        setMenu({ id: null, kind: null });
        setRemoving({ row, section, record: null });
        try {
          const [company, nextLines] = await Promise.all([
            api(`/companies/${row.company_id}`),
            api(`/companies/${row.company_id}/actions`).catch(() => null),
          ]);
          const contact = row.contact_id ? { id: row.contact_id } : null;
          setRemoving((r) => (r && r.row.company_id === row.company_id ? { ...r, record: { company, contact, act: nextLines } } : r));
        } catch (err) {
          setRemoving(null);
          setError(err.message);
        }
      },
      /*
       * A click on the row body. If something is already open on this row — a menu, the
       * why panel, the record — the click closes it, which is what a click outside a
       * popover means everywhere else. Otherwise it opens the record.
       */
      rowClick: (row) =>
        setMenu((m) =>
          m.id === row.company_id && m.kind
            ? { id: null, kind: null }
            : row.contact_id
              ? { id: row.company_id, kind: "detail" }
              : m
        ),
      // A reply owed opens the record with the reply on top of its Next.
      openRecord: (row, section) => {
        const next = { contactId: row.contact_id, label: row.company_name, kind: section === "owed" ? "reply_waiting" : null };
        const cur = recordRef.current;
        if (cur && String(cur.contactId) === String(next.contactId)) setRecord(next);
        else leaveRecord(() => setRecord(next));
      },
    }),
    [act, leaveRecord]
  );

  const bulk = useCallback(
    async (path, body) => {
      await act(path, { ...body, company_ids: selected });
      setChecked({});
    },
    [act, selected]
  );

  // --- keyboard -----------------------------------------------------------
  // Reloaded on the way out, not just closed: the panel can remove the record a
  // row is about ("not a lead" purges the contact, and the company with it when
  // it holds nothing else).
  const closeRecord = useCallback(() => {
    setRecord(null);
    load();
  }, [load]);

  // Esc closes one thing at a time, the most recently opened first: the help,
  // a row's menu, then the record panel (useEscape.js). Never while typing,
  // which is what used to close the panel from inside its composer.
  useEscapeLayer(!!record, () => leaveRecord(closeRecord));
  useEscapeLayer(!!(menu.id && menu.kind), () => setMenu({ id: null, kind: null }));
  useEscapeLayer(showHelp, () => setShowHelp(false));

  useEffect(() => {
    const onKey = (e) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return;
      const tag = (e.target.tagName || "").toLowerCase();
      if (tag === "input" || tag === "textarea" || tag === "select" || e.target.isContentEditable) return;
      // A confirm is open over the page: its keys are its own.
      if (removing) return;

      if (e.key === "?") return setShowHelp((s) => !s);
      if (!flat.length) return;

      if (e.key === "j") return setCursor((c) => Math.min(flat.length - 1, c + 1));
      if (e.key === "k") return setCursor((c) => Math.max(0, c - 1));

      const here = flat[Math.min(cursor, flat.length - 1)];
      if (!here) return;
      if (e.key === "c") return actions.compose(here.row, here.section);
      // 'e' is a one-week snooze with no note: the fast path for "not this week", which is
      // the decision most rows on this page actually need.
      if (e.key === "e") return actions.snooze(here.row.company_id, { preset: "next_monday" });
      if (e.key === "x") return actions.openMenu(here.row.company_id, "ignore");
      if (e.key === "w") return actions.markWorked(here.row.company_id);
      if (e.key === "o") return actions.openMenu(here.row.company_id, "detail");
      // The full record, beside the queue rather than instead of it.
      if (e.key === "f" && here.row.contact_id) return actions.openRecord(here.row, here.section);
      if (e.key === " ") {
        e.preventDefault();
        return actions.toggleCheck(here.row.company_id);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flat, cursor, actions, removing]);

  useEffect(() => {
    if (cursorRef.current) cursorRef.current.scrollIntoView({ block: "nearest" });
  }, [cursor]);

  if (error && !data) {
    return (
      <>
        <Topbar title="Follow-ups" />
        <div className="crm-empty">Could not load follow-ups: {error}</div>
      </>
    );
  }
  if (!data) {
    return (
      <>
        <Topbar title="Follow-ups" />
        <div className="crm-empty">Working out what has been sitting…</div>
      </>
    );
  }

  const { counts, vocabulary, queues } = data;
  const rendering = queues.filter((q) => q.renders);
  const toWork = counts.owed + counts.scheduled + counts.queued;
  const allClear = view === "queues" && toWork === 0 && counts.worked_today > 0;
  // The first capped queue with more matches than today's batch — what "pull more" offers.
  const pullable = rendering.find((q) => q.capped) || null;

  const railItem = (key, label, count, onClick, active) => (
    <button
      key={key}
      type="button"
      className={`crm-fu__rail-item${active ? " is-active" : ""}`}
      onClick={onClick}
    >
      <span className="crm-fu__grip" aria-hidden="true">⠿</span>
      <span className="crm-fu__rail-label">{label}</span>
      <span className="crm-fu__rail-count crm-num">{count}</span>
    </button>
  );

  const scrollTo = (slug) => {
    const el = document.getElementById(`fu-${slug}`);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
    setView("queues");
    setHere(slug);
  };

  let index = -1;

  return (
    <>
      <Topbar
        title="Follow-ups"
        sub={`${toWork} to work today · ${counts.scheduled} scheduled · ${TODAY_LONG()}`}
      >
        {/* WHEN THE FACTS LAST MOVED, beside the button that moves them. Every row here
            is derived from the activity sweep, so a page that looks current while the
            sweep has been dead since Tuesday is the one failure a queue cannot survive —
            and the stamp is the sweep's own, not this request's clock. */}
        <span className="crm-fu__synced crm-num">{syncedLabel(data.synced_at)}</span>
        <button type="button" className="crm-btn crm-btn--secondary" onClick={load} disabled={busy}>
          Refresh
        </button>
      </Topbar>

      {/* The queue narrows when the record opens rather than the record covering it —
          the same split as Approvals, and for the same reason: what you are deciding
          about and what you are deciding with both have to stay on screen. */}
      <div className={`crm-fu${record ? " crm-fu--split" : ""}`}>
        <aside className="crm-fu__rail">
          <div className="crm-fu__rail-head">Queues</div>
          <div className="crm-fu__rail-list">
            {railItem("scheduled", "Scheduled for today", counts.scheduled, () => scrollTo("scheduled"), here === "scheduled")}
            {railItem("owed", "You owe a reply", counts.owed, () => scrollTo("owed"), here === "owed")}
            {queues.map((q) =>
              railItem(
                q.slug,
                q.name,
                // The TRUE count, even for a rule that is off or out of cadence today —
                // that is how you decide whether turning it on is worth it.
                q.count,
                () => scrollTo(q.slug),
                here === q.slug
              )
            )}
          </div>

          <Link className="crm-fu__newrule" to="/follow-ups/rules/new">
            + new rule
          </Link>

          <div className="crm-fu__rail-foot">
            <button
              type="button"
              className={`crm-fu__rail-item crm-fu__rail-item--muted${view === "muted" ? " is-active" : ""}`}
              onClick={() => setView(view === "muted" ? "queues" : "muted")}
            >
              <span className="crm-fu__rail-label">Muted</span>
              <span className="crm-fu__rail-count crm-num">{counts.muted}</span>
            </button>
            <div className="crm-fu__rail-hint">
              Muted companies never enter a queue until you un-mute them. Nothing is ever
              unrecoverably hidden.
            </div>
          </div>
        </aside>

        <main className="crm-fu__main">
          {view === "muted" && <MutedTable rows={data.muted} onUnmute={(id) => act(`/follow-ups/${id}/unmute`)} />}

          {allClear && (
            <AllClear
              counts={counts}
              pullable={pullable}
              onPullMore={(ruleId) => act(`/follow-up-rules/${ruleId}/pull-more`)}
            />
          )}

          {view === "queues" && !allClear && (
            <>
              {PINNED.map((section) => {
                const rows = data[section.key] || [];
                if (!rows.length) return null;
                return (
                  <section key={section.key} id={`fu-${section.key}`} className="crm-fu__sect crm-fu__sect--pinned">
                    <header className="crm-fu__pinhead">
                      <span className="crm-fu__pinflag">Pinned</span>
                      <span className="crm-fu__title">{section.title}</span>
                      <span className="crm-num">{rows.length}</span>
                      <span className="crm-fu__note-r">{section.note}</span>
                    </header>
                    {rows.map((row) => {
                      index += 1;
                      const at = index;
                      return (
                        <div key={row.company_id} ref={at === cursor ? cursorRef : null} className={at === cursor ? "crm-fu__cursor" : ""}>
                          <Row
                            row={row}
                            section={section.key}
                            ui={{
                              checked: !!checked[row.company_id],
                              open: menu.id === row.company_id ? menu.kind : null,
                              vocabulary,
                            }}
                            actions={actions}
                          />
                        </div>
                      );
                    })}
                  </section>
                );
              })}

              {queues.map((q) => {
                const isCollapsed = !!collapsed[q.slug];
                return (
                  <section key={q.slug} id={`fu-${q.slug}`} className="crm-fu__sect">
                    <header className="crm-fu__head">
                      <div>
                        <div className="crm-fu__headline">
                          <span className="crm-fu__title">{q.name}</span>
                          <span className="crm-num crm-fu__count">{q.count}</span>
                          {!q.enabled && <span className="crm-fu__chip crm-fu__chip--state">off</span>}
                          {q.enabled && !q.renders && !q.errors.length && (
                            <span className="crm-fu__chip crm-fu__chip--state">not today · {q.cadence}</span>
                          )}
                        </div>
                        {q.description && <div className="crm-fu__desc">{q.description}</div>}
                      </div>
                      <div className="crm-fu__headacts">
                        <Link className="crm-fu__editrule" to={`/follow-ups/rules/${q.rule_id}`}>
                          edit rule
                        </Link>
                        <button
                          type="button"
                          className="crm-fu__caret"
                          onClick={() => setCollapsed((c) => ({ ...c, [q.slug]: !c[q.slug] }))}
                        >
                          {isCollapsed ? "▸" : "▾"}
                        </button>
                      </div>
                    </header>

                    {/* A rule broken since it was saved says so, loudly. An empty queue that
                        is empty because of a typo reads as "no work to do", which is the
                        answer Gabriel would act on and exactly the answer that is wrong. */}
                    {!!q.errors.length && (
                      <div className="crm-fu__ruleerr">
                        This rule is switched off by an error: {q.errors.join(" ")}
                      </div>
                    )}

                    {!isCollapsed && !q.errors.length && q.renders && (
                      <>
                        {q.rows.map((row) => {
                          index += 1;
                          const at = index;
                          return (
                            <div key={row.company_id} ref={at === cursor ? cursorRef : null} className={at === cursor ? "crm-fu__cursor" : ""}>
                              <Row
                                row={row}
                                section={q.slug}
                                ui={{
                                  checked: !!checked[row.company_id],
                                  open: menu.id === row.company_id ? menu.kind : null,
                                  vocabulary,
                                }}
                                actions={actions}
                              />
                            </div>
                          );
                        })}

                        {q.capped && (
                          <div className="crm-fu__capfoot">
                            <span className="crm-num">
                              showing {q.rows.length} of {q.count}
                            </span>
                            <button type="button" className="crm-fu__link" onClick={() => act(`/follow-up-rules/${q.rule_id}/pull-more`)}>
                              pull {q.cap} more
                            </button>
                            <span className="crm-fu__capnote">capped at {q.cap} a day so it ends</span>
                          </div>
                        )}

                        {/*
                          WHAT THIS QUEUE MATCHED AND IS NOT DRAWING. A company renders in
                          the one queue that scores it highest, so a rule can match a row
                          and still show nothing — and a count in the header with no rows
                          under it reads as a bug unless the page says where they went.
                        */}
                        {q.shown_elsewhere > 0 && !!q.rows.length && (
                          <div className="crm-fu__elsewhere">{elsewhereNote(q)}</div>
                        )}

                        {!q.rows.length && (
                          <div className="crm-fu__none">
                            <b>
                              {q.worked_today
                                ? `All ${q.worked_today} worked.`
                                : q.shown_elsewhere > 0
                                  ? "Nothing here that isn't shown above."
                                  : "Nothing in here today."}
                            </b>
                            <span>
                              {q.worked_today
                                ? "Tomorrow's batch is chosen the first time you open this page."
                                : q.shown_elsewhere > 0
                                  ? elsewhereNote(q)
                                  : "No company matches this rule right now."}
                            </span>
                            <Link to={`/follow-ups/rules/${q.rule_id}`}>loosen the rule</Link>
                          </div>
                        )}
                      </>
                    )}

                    {isCollapsed && (
                      <button
                        type="button"
                        className="crm-fu__collapsed"
                        onClick={() => setCollapsed((c) => ({ ...c, [q.slug]: false }))}
                      >
                        collapsed · {q.count} match{q.count === 1 ? "" : "es"}
                        {q.cap ? `, top ${q.cap} surface when you open it` : ""}
                      </button>
                    )}
                  </section>
                );
              })}

              <div className="crm-fu__foot">
                That's every queue for today.{" "}
                {counts.matched - counts.queued > 0 &&
                  `${counts.matched - counts.queued} companies sit outside the caps — they come up on the days their rules pick them.`}
              </div>
            </>
          )}
        </main>

        {record && (
          /* Keyed on the contact so switching rows remounts, rather than showing the
             previous person's timeline while the next one loads. */
          <aside className="crm-peek crm-fu__peek">
            <ContactDetailPage
              key={record.contactId}
              contactId={record.contactId}
              embedded
              onClose={closeRecord}
              // A send from the panel works the row behind it.
              onChange={load}
              // Not while the queue's own composer is open for this person: one
              // composer per email.
              openAction={replying && replying.row.contact_id === record.contactId ? null : record.kind}
              hostLabel="Follow-ups"
              guardRef={recordGuard}
            />
          </aside>
        )}
      </div>

      {/* Bulk actions. Clearing a backlog happens in one sitting or not at all, so the
          reason picker is inline rather than behind another dialog. */}
      {selected.length > 0 && (
        <BulkBar
          count={selected.length}
          vocabulary={vocabulary}
          busy={busy}
          onSnooze={(body) => bulk("/follow-ups/bulk-snooze", body)}
          onIgnore={(body) => bulk("/follow-ups/bulk-ignore", body)}
          onWorked={() => bulk("/follow-ups/bulk-worked", {})}
          onClear={() => setChecked({})}
        />
      )}

      {showHelp && (
        <div className="crm-fu__help" onClick={() => setShowHelp(false)}>
          <div className="crm-fu__help-card">
            <b>Keyboard</b>
            <div><kbd>j</kbd> / <kbd>k</kbd> move</div>
            <div><kbd>c</kbd> compose</div>
            <div><kbd>e</kbd> snooze to next Monday</div>
            <div><kbd>x</kbd> ignore</div>
            <div><kbd>w</kbd> mark worked</div>
            <div><kbd>o</kbd> open the record in place · or click the row</div>
            <div><kbd>f</kbd> the full record, beside the queue</div>
            <div><kbd>space</kbd> select</div>
            <div><kbd>?</kbd> this help · <kbd>esc</kbd> close</div>
          </div>
        </div>
      )}

      {removing && removing.record && (
        <div className="rec-dialog__backdrop" onClick={() => setRemoving(null)}>
          <div className="rec-dialog" onClick={(e) => e.stopPropagation()}>
            <ConfirmCard
              record={removing.record}
              endingId="remove"
              ops={{
                runWrites: async (writes) => {
                  for (const w of withRowWrites(writes, { companyId: removing.row.company_id, section: removing.section })) {
                    await api(w.path, { method: w.method, body: w.body === undefined ? undefined : JSON.stringify(w.body) });
                  }
                },
              }}
              onCancel={() => setRemoving(null)}
              onDone={() => {
                setRemoving(null);
                load();
              }}
            />
          </div>
        </div>
      )}

      {replying && (
        <div className="crm-fu__panel">
          <ReplyPanel
            action={{
              kind: replying.section === "owed" ? "reply_waiting" : "nudge_due",
              verb: replying.section === "owed" ? "Reply" : "Nudge",
              contact_id: replying.row.contact_id,
              contact_email: replying.row.contact_email,
              age_days: replying.row.quiet_days,
            }}
            onClose={() => {
              setReplying(null);
              load();
            }}
          />
        </div>
      )}
    </>
  );
}

function BulkBar({ count, vocabulary, busy, onSnooze, onIgnore, onWorked, onClear }) {
  const [reason, setReason] = useState("");

  return (
    <div className="crm-fu__bulk">
      <span className="crm-fu__bulk-n">{count} selected</span>
      <div className="crm-fu__bulk-acts">
        <button type="button" className="crm-fu__btn crm-fu__btn--do" disabled={busy} onClick={onWorked}>
          Mark worked
        </button>
        <button type="button" className="crm-fu__btn" disabled={busy} onClick={() => onSnooze({ preset: "next_monday" })}>
          Snooze to Monday
        </button>
        <select className="crm-select" value={reason} onChange={(e) => setReason(e.target.value)}>
          <option value="">Ignore, reason…</option>
          {(vocabulary.ignore_reasons || []).map((r) => (
            <option key={r} value={r}>
              {REASON_LABELS[r] || r}
            </option>
          ))}
        </select>
        <button
          type="button"
          className="crm-fu__btn"
          disabled={busy || !reason}
          onClick={() => onIgnore({ days: 90, reason })}
        >
          Ignore 90d
        </button>
        <button type="button" className="crm-fu__link" onClick={onClear}>
          Clear
        </button>
      </div>
    </div>
  );
}
